
import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Loader2 } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "@/hooks/use-toast";
import { User } from "@supabase/supabase-js";
import { profileSchema, type ProfileData } from "@/lib/validations";

interface ProfileFormProps {
  user: User;
  initialProfile: {
    name: string | null;
    phone: string | null;
  };
  onProfileUpdate: () => Promise<void>;
}

export function ProfileForm({
  user,
  initialProfile,
  onProfileUpdate
}: ProfileFormProps) {
  const [loading, setLoading] = useState(false);
  const [profile, setProfile] = useState(initialProfile);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setProfile({
      ...profile,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      // Validate form data using Zod
      const validatedData = profileSchema.parse(profile);
      
      // Update profile in the profiles table
      const { error: profileError } = await supabase
        .from("profiles")
        .update({
          name: validatedData.name,
          phone: validatedData.phone,
          updated_at: new Date().toISOString()
        })
        .eq("id", user.id);
        
      if (profileError) throw profileError;

      // Update user metadata to keep consistent data
      const { error: metadataError } = await supabase.auth.updateUser({
        data: { 
          name: validatedData.name,
          phone: validatedData.phone 
        }
      });
      
      if (metadataError) throw metadataError;

      // Update session user metadata
      await onProfileUpdate();
      
      toast({
        title: "Perfil atualizado",
        description: "Suas informações foram atualizadas com sucesso."
      });
    } catch (error: any) {
      let errorMessage = "Falha ao atualizar o perfil";
      
      if (error.errors && Array.isArray(error.errors)) {
        // Zod validation errors
        errorMessage = error.errors.map((err: any) => err.message).join(", ");
      } else if (error.message) {
        errorMessage = error.message;
      }
      
      toast({
        title: "Erro na validação",
        description: errorMessage,
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={handleSubmit}>
      <div className="space-y-4">
        <div className="space-y-2">
          <label htmlFor="name" className="text-sm font-medium text-gray-300">
            Nome
          </label>
          <Input 
            id="name" 
            name="name" 
            value={profile.name || ""} 
            onChange={handleChange} 
            placeholder="Seu nome" 
            className="border-red-700 bg-gray-600" 
            required
          />
        </div>
        
        <div className="space-y-2">
          <label htmlFor="phone" className="text-sm font-medium text-gray-300">
            Telefone
          </label>
          <Input 
            id="phone" 
            name="phone" 
            value={profile.phone || ""} 
            onChange={handleChange} 
            placeholder="(11) 99999-9999" 
            className="border-red-700 bg-gray-600" 
            required
          />
        </div>
      </div>
      
      <div className="pt-6 pb-0 flex justify-end">
        <Button 
          type="submit" 
          disabled={loading} 
          className="bg-krecao-yellow text-black hover:bg-amber-500"
        >
          {loading ? (
            <>
              <Loader2 size={16} className="animate-spin mr-2" />
              Salvando...
            </>
          ) : (
            "Salvar Alterações"
          )}
        </Button>
      </div>
    </form>
  );
}
